<template>
  <div class="actions-history-section">
    <s-table
      class="actions-history-section__table"
      :fields="fields"
      :items="items"
      :busy="loadingTable"
    >
      <template
        v-for="field in fields"
        #[`cell(${field.key})`]="data"
      >
        {{ data.value || '-' }}
      </template>
      <template #cell(eventDate)="data">
        {{ data.item.eventDate | dateTimeFilter({ format: dateFormat }) }}
      </template>
      <template #cell(identifiedByIed)="data">
        {{ data.item.identifiedByIed ? $t('general.yes') : $t('general.no') }}
      </template>
      <template #cell(informedMaintenance)="data">
        {{ data.item.informedMaintenance ? $t('general.yes') : $t('general.no') }}
      </template>
      <template #cell(maintenanceDate)="data">
        {{ data.item.maintenanceDate | dateTimeFilter({ format: dateFormat }) }}
      </template>
      <template
        class="hide-on-print"
        #cell(actions)="data"
      >
        <b-button
          variant="primary"
          size="sm"
          @click="onVisualizeActionClickHandler(data.item)"
        >
          {{ $t('general.view') }}
        </b-button>
        <b-dropdown
          v-if="$hasUserPermission('PER013')"
          class="actions-history-section__options-btn"
          variant="link"
          offset="-15"
          right
          no-caret
        >
          <template slot="button-content">
            <i
              aria-hidden="true"
              class="fas fa-ellipsis-v text-gray"
            />
          </template>
          <b-dropdown-item @click="onRemoveActionClickHandler(data.item)">
            <span class="text-danger">
              <i
                class="fa fa-trash"
                aria-hidden="true"
              >
              </i> {{ $t('engineering.commutator-maintenance.delete-action') }}
            </span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </s-table>
    <grid-paginator
      class="display-none"
      v-model="pageRequest.page"
      :total-rows="totalItems"
      :per-page="pageRequest.pageSize"
      @input="paging()"
    />
    <maintenance-action-modal
      v-model="maintenanceActionModalShow"
      visualization-mode
      :maintenance="selectedMaintenance"
    />
    <remove-ied-action-modal
      v-model="removeActionModalShow"
      :is-loading="loading"
      @cancel="onRemoveActionCancelHandler"
      @remove-action="onRemoveActionHandler"
    />
  </div>
</template>

<script>
import LoadingMixin from '@/services/loadingMixin';
import enumFilter from '@/filters/enumFilter';
import dateTimeFilter from '@/filters/dateTimeFilter';
import { formats } from '@/constants/localizer';
import GridPaginator from '@/components/lists/Paginator';
import Service
  from '@/domain/services/installation/engineering/commutatorMaintenance/commutatorMaintenance';
import actionHistoryFields
  from '@/domain/data/installation/engineering/commutatorMaintenance/actionHistoryFields';
import MaintenanceActionModal
  from '@/domain/components/installation/engineering/commutatorMaintenance/modal/MaintenanceActionModal';
import RemoveIedActionModal
  from '@/domain/components/installation/engineering/commutatorMaintenance/modal/RemoveIedActionModal';

export default {
  name: 'ActionsHistorySection',
  props: {
    assetModuleId: {
      type: String,
      require: true,
    },
    commutatorId: {
      type: [String, Number],
      require: true,
    },
    loadingTable: {
      type: Boolean,
      defaut: false,
    },
    items: {
      type: Array,
      default: () => ([]),
    },
    totalItems: {
      type: Number,
      default: 0,
    },
  },
  components: {
    GridPaginator,
    MaintenanceActionModal,
    RemoveIedActionModal,
  },
  mixins: [
    LoadingMixin,
  ],
  data: () => ({
    pageRequest: {
      page: 1,
      pageSize: 10,
    },
    maintenanceActionModalShow: false,
    removeActionModalShow: false,
    selectedMaintenance: {},
    selectedMaintenanceAction: null,
  }),
  created() {
    this.paging();
  },
  computed: {
    fields() {
      return actionHistoryFields(this);
    },
    dateFormat() {
      return formats.DATE_SHORT;
    },
  },
  filters: {
    enumFilter,
    dateTimeFilter,
  },
  methods: {
    paging() {
      this.$emit('paging', this.pageRequest);
    },
    onVisualizeActionClickHandler(maintenanceAction) {
      this.startLoading();

      Service.getCommutatorActionById(
        this.assetModuleId,
        this.commutatorId,
        maintenanceAction.id,
      )
        .then((response) => {
          this.selectedMaintenance = response;
          this.maintenanceActionModalShow = true;
        })
        .finally(() => this.endLoading());
    },
    onRemoveActionClickHandler(maintenanceAction) {
      this.selectedMaintenanceAction = maintenanceAction;
      const { identifiedByIed } = this.selectedMaintenanceAction;

      if (identifiedByIed) {
        this.removeIedActionOpen();
        return;
      }

      this.removeNotIedActionOpen();
    },
    removeIedActionOpen() {
      this.removeActionModalShow = true;
    },
    removeNotIedActionOpen() {
      this.$confirmMessage({ code: 'MC011' })
        .accepted(() => {
          this.removeAction();
        }).rejected(() => {
          this.removeActionComponentClose();
        });
    },
    removeActionComponentClose() {
      this.removeActionModalShow = false;
      this.selectedMaintenanceAction = null;
    },
    onRemoveActionCancelHandler() {
      this.removeActionComponentClose();
    },
    onRemoveActionHandler(selectedRemovalActionMode, clearRemovalActionModeCallback) {
      this.removeAction(selectedRemovalActionMode, clearRemovalActionModeCallback);
    },
    removeAction(selectedRemovalActionMode, clearRemovalActionModeCallback) {
      const payload = {
        exclusionMode: selectedRemovalActionMode,
      };

      this.startLoading();

      Service.deleteCommutatorActionById(
        this.assetModuleId,
        this.commutatorId,
        this.selectedMaintenanceAction.id,
        payload,
      )
        .then(() => {
          this.$pushAlert({ code: 'MS007' });
          if (clearRemovalActionModeCallback) {
            clearRemovalActionModeCallback();
          }
          this.removeActionComponentClose();
          this.$emit('refresh');
        })
        .finally(() => this.endLoading());
    },
  },
};
</script>
<style lang="sass" scoped>
  ::v-deep
    .actions-history-section
      &__cell-actions
        white-space: nowrap
    thead tr th
      white-space: normal !important
      padding: 5px 10px
  @media print
    .display-none
      display: none
    ::v-deep .actions-history-section__cell-actions
      display: none
</style>
